// ============================================================
// Enums
// ============================================================

export enum ContactImportState {
  Pending = 0,
  InProgress = 1,
  Validated = 2,
  ImportRequested = 3,
  Imported = 4,
  Failed = 5,
}

export enum ContactImportAction {
  NotSet = 0,
  Skip = 1,
  UpdateMissing = 2,
  OverrideExisting = 3,
  Create = 4,
}

export enum EPhoneType {
  Phone = 0,
  Mobile = 1,
  Fax = 2,
}

// ============================================================
// Base DTO
// ============================================================

export interface ExternalImportDto {
  initiatorContactId: string;
  contactImportProcessId: string;
  createdAt: string;
  updatedAt: string;
  description?: string;
  action: ContactImportAction;
  rowVersion: string;
}

// ============================================================
// Detail-Entity Read DTOs
// ============================================================

export interface ExternalEmailAddressImportDto {
  externalEmailAddressImportId: string;
  externalContactImportId?: string;
  externalBusinessUnitImportId?: string;
  address: string;
  notes: string;
  isPrimary: boolean;
}

export interface ExternalPhoneNumberImportDto {
  externalPhoneNumberImportId: string;
  externalContactImportId?: string;
  externalBusinessUnitImportId?: string;
  number: string;
  notes: string;
  phoneType: EPhoneType;
  isPrimary: boolean;
}

export interface ExternalAddressImportDto {
  externalAddressImportId: string;
  externalContactImportId?: string;
  externalBusinessUnitImportId?: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  region: string;
  secondAddressLine: string;
  notes: string;
  isPrimary: boolean;
}

export interface ExternalBankAccountImportDto {
  externalBankAccountImportId: string;
  externalCompanyImportId?: string;
  externalContactImportId?: string;
  creditInstitutionName: string;
  bic: string;
  iban: string;
  notes: string;
  isPrimary: boolean;
}

// ============================================================
// Main Entity Read DTOs
// ============================================================

export interface ExternalCompanyImportDto extends ExternalImportDto {
  externalCompanyImportId: string;
  legalName: string;
  displayName: string;
  notes: string;
  commercialRegisterNumber: string;
  commercialRegisterCity: string;
  legalForm: string;
  vatNumber: string;
  externalBankAccountImports: ExternalBankAccountImportDto[];
  existingCompanyId: string;
}

export interface ExternalBusinessUnitImportDto extends ExternalImportDto {
  externalBusinessUnitImportId: string;
  externalCompanyImportId?: string;
  displayName: string;
  isPrimary: boolean;
  addresses: ExternalAddressImportDto[];
  phoneNumbers: ExternalPhoneNumberImportDto[];
  emailAddresses: ExternalEmailAddressImportDto[];
  existingBusinessUnitId: string;
}

export interface ExternalContactImportDto extends ExternalImportDto {
  externalContactImportId: string;
  externalCompanyImportId?: string;
  externalBusinessUnitImportId?: string;
  salutation: string;
  firstName: string;
  lastName: string;
  title: string;
  department: string;
  position: string;
  website: string;
  notes: string;
  emailAddresses: ExternalEmailAddressImportDto[];
  phoneNumbers: ExternalPhoneNumberImportDto[];
  addresses: ExternalAddressImportDto[];
  bankAccounts: ExternalBankAccountImportDto[];
  existingContactId: string;
  existingCompanyId: string;
  existingBusinessUnitId: string;
}

// ============================================================
// Process DTO
// ============================================================

export interface ContactImportProcessDto {
  contactImportProcessId: string;
  initiatorContactId: string;
  createdAt: string;
  updatedAt: string;
  description?: string;
  errorMessage?: string | null;
  state: ContactImportState;
  externalCompanyImportsCount: number;
  externalContactImportsCount: number;
  aiNormalizedCount: number;
  externalCompanyImports: ExternalCompanyImportDto[];
  externalContactImports: ExternalContactImportDto[];
  externalBusinessUnitImports: ExternalBusinessUnitImportDto[];
}

// ============================================================
// CSV Mapping DTO
// ============================================================

export interface CsvMappingDto {
  nameSimilarityThreshold?: number;
  maxLevenshteinDistance?: number;
  useAiNormalization: boolean;

  // Company
  companyLegalName?: string;
  companyDisplayName?: string;
  companyNotes?: string;
  companyCommercialRegisterNumber?: string;
  companyCommercialRegisterCity?: string;
  companyLegalForm?: string;
  companyVatNumber?: string;

  // Bank
  bankCreditInstitutionName?: string;
  bankBic?: string;
  bankIban?: string;

  // BusinessUnit
  businessUnitDisplayName?: string;
  businessUnitPhone?: string;
  businessUnitEMail?: string;
  businessUnitStreet?: string;
  businessUnitZip?: string;
  businessUnitCity?: string;
  businessUnitCountry?: string;
  businessUnitRegion?: string;
  businessUnitIsPrimary?: string;

  // Contact
  contactSalutation?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactTitle?: string;
  contactDepartment?: string;
  contactPosition?: string;
  contactWebsite?: string;
  contactNotes?: string;

  // Contact Details
  contactEMail?: string;
  contactEMail2?: string;
  contactEMail3?: string;
  contactPhone?: string;
  contactPhone2?: string;
  contactPhone3?: string;
  contactCellphone?: string;
  contactCellphone2?: string;
  contactCellphone3?: string;
  contactFax?: string;
  contactStreet?: string;
  contactZip?: string;
  contactCity?: string;
  contactCountry?: string;
  contactRegion?: string;
}

// ============================================================
// Update DTOs
// ============================================================

export interface UpdateContactCompanyFromImportProcessDto {
  contactImportProcessId: string;
  updateExternalCompanyImports?: UpdateExternalCompanyImportDto[];
  updateExternalContactImports?: UpdateExternalContactImportDto[];
  updateExternalBusinessUnitImports?: UpdateExternalBusinessUnitImportDto[];
  updateExternalBankAccountImports?: UpdateExternalBankAccountImportDto[];
}

export interface UpdateExternalCompanyImportDto {
  externalCompanyImportId: string;
  legalName: string;
  displayName: string;
  notes: string;
  commercialRegisterNumber: string;
  commercialRegisterCity: string;
  legalForm: string;
  vatNumber: string;
  existingCompanyId: string;
  description: string;
  action: ContactImportAction;
}

export interface UpdateExternalBusinessUnitImportDto {
  externalBusinessUnitImportId: string;
  externalCompanyImportId?: string;
  displayName: string;
  isPrimary: boolean;
  addresses: UpdateExternalAddressImportDto[];
  phoneNumbers: UpdateExternalPhoneNumberImportDto[];
  emailAddresses: UpdateExternalEmailAddressImportDto[];
  existingBusinessUnitId: string;
  description: string;
  action: ContactImportAction;
}

export interface UpdateExternalContactImportDto {
  externalContactImportId: string;
  externalCompanyImportId?: string;
  externalBusinessUnitImportId?: string;
  salutation: string;
  firstName: string;
  lastName: string;
  title: string;
  department: string;
  position: string;
  website: string;
  notes: string;
  emailAddresses: UpdateExternalEmailAddressImportDto[];
  phoneNumbers: UpdateExternalPhoneNumberImportDto[];
  addresses: UpdateExternalAddressImportDto[];
  bankAccounts: UpdateExternalBankAccountImportDto[];
  action: ContactImportAction;
  existingContactId: string;
  existingCompanyId: string;
  existingBusinessUnitId: string;
  description: string;
}

export interface UpdateExternalEmailAddressImportDto {
  externalEmailAddressImportId: string;
  address: string;
  notes: string;
  isPrimary: boolean;
}

export interface UpdateExternalPhoneNumberImportDto {
  externalPhoneNumberImportId: string;
  number: string;
  notes: string;
  phoneType: EPhoneType;
  isPrimary: boolean;
}

export interface UpdateExternalAddressImportDto {
  externalAddressImportId: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  region: string;
  secondAddressLine: string;
  notes: string;
  isPrimary: boolean;
}

export interface UpdateExternalBankAccountImportDto {
  externalBankAccountImportId: string;
  creditInstitutionName: string;
  bic: string;
  iban: string;
  notes: string;
  isPrimary: boolean;
}

// ============================================================
// Helper constants
// ============================================================

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
